import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from 'react';
import { TargetSelector, TargetSelectorUi, type TargetSelectorMode } from '../TargetSelector';
import { StrikeWeightRatioDesignMode, type StrikeWeightRatioDesignSmoothTarget, type StrikeWeightRatioDesignTarget } from '@/lib/piano/touch-design/design/strike-weight-ratio-design';
import { type StrikeWeightRatioDesignProps } from './StrikeWeightRatioDesign.types';
import { useKeyboardStats } from '@/components/analyze/key-reading/hooks/use-keyboard-stats';
import { StrikeWeightRatioChart } from '@/components/charts/StrikeWeightRatioChart';
import { useTranslation } from '@/hooks/use-translation';
import { formatRatio, formatSignedRatio } from '@/lib/format/format-weight';
import { SmoothStrategy } from '@/lib/geometry/curve-smoother/smooth-strategy.enum';
import type { KeyWith } from '@/lib/piano/keyboard';
import type { OptionalNumber } from '@/lib/piano/touch-design/measured-key.requirements';
import type { TouchWeightKeyAnalysis } from '@/lib/piano/touch-design/touch-weight-key-analysis';
import { cn } from '@/lib/utils';
import { DesignLayout } from '../DesignLayout';
import { useTargetSeries } from '../hooks/use-target-series';
import { useStrikeWeightRatioRecommendation } from './hooks/use-strike-weight-ratio-recommendation';
import { useStrikeWeightRatioTargetSelector } from './hooks/use-strike-weight-ratio-target-selector';
import { useTargetReading } from '../hooks/use-target-reading';
import { NotEnoughData } from '../NotEnoughData';

/** What a constant ratio can be nudged to, and by how much. */
const RATIO_BOUNDS = {
  max: 8,
  min: 4,
  step: 0.1
} as const;

/**
 * The ratio the design works from.
 *
 * It is what you measured where you measured it, and what the analysis derives
 * everywhere else. The smoothing modes average this very quantity, so a median
 * or a mean stated on this page is the one a mode would actually apply.
 */
const selectStrikeWeightRatio = (key: KeyWith<Pick<TouchWeightKeyAnalysis, 'strikeWeightRatio'>>): OptionalNumber => key.payload.strikeWeightRatio;

/**
 * Median or mean, as one segment rather than a dropdown.
 *
 * A choice between two values does not need a popover: the segment states both
 * at once and stays inside the card, in the same frame as the steppers of the
 * other modes.
 */
const SmoothStrategySegment = t0 => {
  const $ = _c(12);
  const {
    label,
    onChange,
    options,
    selected
  } = t0;
  let t1;
  if ($[0] !== onChange) {
    t1 = target => () => {
      onChange(target);
    };
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const select = t1;
  let t2;
  if ($[2] !== options || $[3] !== select || $[4] !== selected) {
    let t3;
    if ($[6] !== select || $[7] !== selected) {
      t3 = option => {
        const isSelected = option.value === selected;
        return <button key={option.value} type="button" aria-pressed={isSelected} onClick={select(option.value)} className={cn("h-10 cursor-pointer px-4 text-[13.5px] font-medium transition-colors", isSelected ? "bg-primary text-primary-foreground" : "bg-card text-foreground hover:bg-accent")}>{option.label}</button>;
      };
      $[6] = select;
      $[7] = selected;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = options.map(t3);
    $[2] = options;
    $[3] = select;
    $[4] = selected;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[9] !== label || $[10] !== t2) {
    t3 = <div role="group" aria-label={label} className="inline-flex overflow-hidden rounded-md border border-input">{t2}</div>;
    $[9] = label;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
export const StrikeWeightRatioDesign: React.FC<StrikeWeightRatioDesignProps> = t0 => {
  const $ = _c(69);
  const {
    analyzedKeyboard,
    requiredDataPercentage,
    notEnoughDataErrorTitle,
    notEnoughDataErrorDescription
  } = t0;
  const {
    currentLanguage,
    t
  } = useTranslation();
  const {
    onModeChange,
    onTargetChange,
    strikeWeightRatioDesignMode,
    strikeWeightRatioDesignTarget,
    updateStrikeWeightRatioDesign
  } = useStrikeWeightRatioTargetSelector();
  useStrikeWeightRatioRecommendation(strikeWeightRatioDesignMode, strikeWeightRatioDesignTarget, updateStrikeWeightRatioDesign);
  const {
    strikeWeightRatioTargetSerie: targetSerie
  } = useTargetSeries();
  let t1;
  if ($[0] !== analyzedKeyboard || $[1] !== targetSerie) {
    t1 = {
      format: formatRatio,
      formatDifference: formatSignedRatio,
      keyboard: analyzedKeyboard,
      targetSerie
    };
    $[0] = analyzedKeyboard;
    $[1] = targetSerie;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const renderReading = useTargetReading(t1);
  const stats = useKeyboardStats(analyzedKeyboard, selectStrikeWeightRatio);
  let t2;
  if ($[3] !== analyzedKeyboard) {
    t2 = analyzedKeyboard.mapToArray(_temp).filter(_temp2);
    $[3] = analyzedKeyboard;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const hasEnoughData = t2.length >= Math.round(analyzedKeyboard.size * requiredDataPercentage);
  let t3;
  if ($[5] !== currentLanguage) {
    t3 = value => formatRatio(value, currentLanguage);
    $[5] = currentLanguage;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const ratioLabelFormatter = t3;
  let t4;
  if ($[7] !== t) {
    t4 = (currentTarget, onChange) => <SmoothStrategySegment label={t("Computation")} onChange={onChange} options={[{
      label: t("Median"),
      value: SmoothStrategy.Median
    }, {
      label: t("Mean"),
      value: SmoothStrategy.Mean
    }]} selected={currentTarget === SmoothStrategy.Median || currentTarget === SmoothStrategy.Mean ? currentTarget : null} />;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const smoothStrategySelectorFactory = t4;
  let t5;
  if ($[9] !== t) {
    t5 = t("Use measured values");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("Leave the strike weight ratio as measured.");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      selectorUi: TargetSelectorUi.UniqueTargetSelector,
      target: null
    };
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t5 || $[15] !== t6) {
    t8 = {
      description: t5,
      label: t6,
      options: t7,
      value: StrikeWeightRatioDesignMode.AsMeasured
    };
    $[14] = t5;
    $[15] = t6;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== currentLanguage || $[18] !== stats.mean || $[19] !== stats.median || $[20] !== t) {
    t9 = t("Compute the median ({{median}}) or the mean ({{mean}}) of your measurements.", {
      mean: formatRatio(stats.mean, currentLanguage),
      median: formatRatio(stats.median, currentLanguage)
    });
    $[17] = currentLanguage;
    $[18] = stats.mean;
    $[19] = stats.median;
    $[20] = t;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t) {
    t10 = t("Compute a value from your measurements");
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t) {
    t11 = t("Computation");
    $[24] = t;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== smoothStrategySelectorFactory || $[27] !== t11) {
    t12 = {
      componentFactory: smoothStrategySelectorFactory,
      placeholder: t11,
      selectorUi: TargetSelectorUi.CustomComponentTargetSelector
    };
    $[26] = smoothStrategySelectorFactory;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t10 || $[30] !== t12 || $[31] !== t9) {
    t13 = {
      description: t9,
      label: t10,
      options: t12,
      value: StrikeWeightRatioDesignMode.Smoothed
    };
    $[29] = t10;
    $[30] = t12;
    $[31] = t9;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t) {
    t14 = t("Use a constant strike weight ratio for all keys.");
    $[33] = t;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t) {
    t15 = t("From 4.0 to 8.0, in steps of 0.1.");
    $[35] = t;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t) {
    t16 = t("Choose a target strike weight ratio.");
    $[37] = t;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t) {
    t17 = t("Target ratio");
    $[39] = t;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== ratioLabelFormatter || $[42] !== t17) {
    t18 = {
      labelFormatter: ratioLabelFormatter,
      maxCharacters: 4,
      maxValue: RATIO_BOUNDS.max,
      minValue: RATIO_BOUNDS.min,
      placeholder: t17,
      selectorUi: TargetSelectorUi.NumericSelector,
      step: RATIO_BOUNDS.step
    };
    $[41] = ratioLabelFormatter;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t14 || $[45] !== t15 || $[46] !== t16 || $[47] !== t18) {
    t19 = {
      description: t14,
      hint: t15,
      label: t16,
      options: t18,
      value: StrikeWeightRatioDesignMode.FixedValue
    };
    $[44] = t14;
    $[45] = t15;
    $[46] = t16;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t13 || $[50] !== t19 || $[51] !== t8) {
    t20 = [t8, t13, t19];
    $[49] = t13;
    $[50] = t19;
    $[51] = t8;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  const targetSelectorModes = t20;
  let t21;
  if ($[53] !== targetSerie.data) {
    t21 = targetSerie.data.some(_temp3);
    $[53] = targetSerie.data;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  const hasTarget = t21;
  let t22;
  if ($[55] !== analyzedKeyboard || $[56] !== hasEnoughData || $[57] !== hasTarget || $[58] !== notEnoughDataErrorDescription || $[59] !== notEnoughDataErrorTitle || $[60] !== onModeChange || $[61] !== onTargetChange || $[62] !== renderReading || $[63] !== strikeWeightRatioDesignMode || $[64] !== strikeWeightRatioDesignTarget || $[65] !== t || $[66] !== targetSelectorModes || $[67] !== targetSerie) {
    t22 = !hasEnoughData ? <NotEnoughData title={notEnoughDataErrorTitle} description={notEnoughDataErrorDescription} /> : <DesignLayout objective={<TargetSelector title={t("Target")} modes={targetSelectorModes} selectedMode={strikeWeightRatioDesignMode} selectedTarget={strikeWeightRatioDesignTarget} onModeChange={onModeChange} onTargetChange={onTargetChange} />} chart={<StrikeWeightRatioChart renderReading={renderReading} variant="design" keyboard={analyzedKeyboard} targetSerie={targetSerie} displayBothManualAndComputed={false} extraLegendItems={hasTarget ? [{
      colorClassName: "text-ptl-series-target",
      id: "target",
      label: t("Target"),
      mark: "line-thick" as const
    }] : undefined} />} />;
    $[55] = analyzedKeyboard;
    $[56] = hasEnoughData;
    $[57] = hasTarget;
    $[58] = notEnoughDataErrorDescription;
    $[59] = notEnoughDataErrorTitle;
    $[60] = onModeChange;
    $[61] = onTargetChange;
    $[62] = renderReading;
    $[63] = strikeWeightRatioDesignMode;
    $[64] = strikeWeightRatioDesignTarget;
    $[65] = t;
    $[66] = targetSelectorModes;
    $[67] = targetSerie;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  return t22;
};
function _temp(key) {
  return key.payload.strikeWeightRatio;
}
function _temp2(v) {
  return v !== undefined && v !== null;
}
function _temp3(point) {
  return point.payload !== undefined && !Number.isNaN(point.payload);
}